import * as React from 'react'
import { browserHistory, InjectedRouter, PlainRoute } from 'react-router'
import { inject, observer } from 'mobx-react'

import { TemplateSection } from './TemplateSection'
import { BasicInformationSection } from './BasicInformationSection'
import { DetailsSection } from './DetailsSection'
import { InventorySection } from './InventorySection'
import { VariantsSection } from './VariantsSection'
import { WarningSection } from './WarningSection'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { CurrentUserStore } from '../../../../stores/CurrentUserStore'
import { LoadingAnimation } from '../../../../components/LoadingAnimation'
import SampleDirectLogo from '../../../../images/sample-direct.png'
import {
  API,
  IBaseProduct,
  PermissionNames,
  IPricingGroup,
  INestedProduct,
} from '@getgreenline/homi-shared'
import {
  CreateProduct,
  CreateChildProduct,
  MetaDataReservedKeys,
  ProductMetaDataItem,
  ProductMetaData,
} from './ProductStore'
import { Routes } from '../../../../constants/Routes'
import AppHeader from '../../../../components/AppView/Header'
import { AppView } from '../../../../components/AppView'
import { UploadImageSection } from './Components/UploadImageSection'
import { HelpLinkMenu } from '../../../../components/GrDropdown/HelpLinkMenu/HelpLinkMenu'
import { BlazeButton } from '../../../../components/CustomButtons/BlazeButton'
import './index.scss'
import Helmet from 'react-helmet'
import { Track } from '../../../../Track'
import {
  GrRegex,
  isSystemProduct,
  parseErrorMsg,
  uploadImageAndGetCloudinaryUrl,
} from '../../../../utilities/helpers'
import { ParentWarning } from './ParentWarning'
import { CLOUDINARY } from '../../../../constants/general'
import { MetaDataModels, ProvinceProductModels, UnitModels } from '@getgreenline/products'
import { LocalStorage } from '../../../../utilities/LocalStorage'
import { VariantStatusSection } from './VariantStatusSection/VariantStatusSection'
import { message, Row, Col } from 'antd-v4'
import { BlazeAlert } from '../../../../components/BlazeAlert/BlazeAlert'
import { HelpLinks } from '../../../../constants/ExternalLinks'
import { DashboardImageCard } from '../../DashboardHome/DashboardImageCards/DashboardImageCard'
import { IGlobalProduct } from '@getgreenline/global-products'
import {
  getSkuFromGlobalProduct,
  getSkuFromGlobalProductV2,
} from '../../../../utilities/globalProduct'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
  params?: any
  router: InjectedRouter
  route: PlainRoute
}

interface State {
  isSaving: boolean
  errorMessage?: string
  showImportProducts: boolean
  productsWithDuplicateSku: IBaseProduct[]
  savingBlocked: boolean
  savingBlockedCannabs: boolean
  pricingGroups: IPricingGroup[]
  parentProduct?: INestedProduct
  isImageUploadingToCloudinary: boolean
  activeKeys: string[]
  variantsSectionRef: HTMLDivElement | null
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class AddProduct extends React.Component<Props, State> {
  product = new CreateProduct()
  company = this.props.currentCompanyStore!.company!

  constructor(props: Props) {
    super(props)
    this.state = {
      errorMessage: undefined,
      isSaving: false,
      showImportProducts: false,
      productsWithDuplicateSku: [],
      savingBlocked: false,
      savingBlockedCannabs: false,
      pricingGroups: [],
      parentProduct: undefined,
      isImageUploadingToCloudinary: false,
      activeKeys: [],
      variantsSectionRef: null,
    }
  }

  componentDidMount = async () => {
    const hasEditPermissions = this.props.currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_EDIT_PRODUCTS,
      this.company.id,
    )

    // If user doesn't have permissions, take them back to the product list page
    if (!hasEditPermissions) {
      browserHistory.push(Routes.DASHBOARD_PRODUCTS(this.company.id))
    }

    const documentBody = document.body as any
    documentBody.className += ' modal-open'
    const editProductId = this.props.params.productId
    if (editProductId) {
      await this.loadProduct(editProductId)
      if (LocalStorage.getOpenVariantsByDefault()) {
        this.setState({ activeKeys: this.allActiveKeys })
      }
    } else {
      this.setState({ activeKeys: ['0'] })
      // On initial ADD product page, set the default inventory mode to 'prepack
      this.product.setInventoryMode('prepack')

      if (this.product.childProducts.length === 0) {
        const newChildProduct = new CreateChildProduct(this.product)
        this.product.childProducts = [newChildProduct]
      }
    }

    const { productStore, supplierStore } = this.props.currentCompanyStore!

    if (!productStore!.categories) {
      productStore!.getCategories()
    }

    if (!supplierStore?.suppliers) {
      supplierStore!.getSuppliers()
    }

    const pricingGroups = await API.getPricingGroups(this.company.id)
    this.setState({ pricingGroups })

    this.props.currentCompanyStore?.getTags()
  }

  componentDidUpdate() {
    this.props.router.setRouteLeaveHook(this.props.route, this.routerWillLeave)

    // Detecting tab close/refresh
    if (this.product.changed)
      window.onbeforeunload = () => {
        return true
      }
  }

  routerWillLeave = () => {
    if (this.product.changed) {
      return 'All unsaved changes will be discarded. Are you sure?'
    }
  }

  UNSAFE_componentWillReceiveProps(newProps: Props) {
    const editProductId = newProps.params.productId
    if (editProductId !== this.product.id) {
      this.loadProduct(editProductId)
    }
  }

  componentWillUnmount() {
    const documentBody = document.body as any
    documentBody.className.replace('modal-open', '')
  }

  setActiveKeys = (activeKeys: string[]) => this.setState({ activeKeys })

  searchDuplicateSkus = (inputSku: string, productResults: IBaseProduct[]) => {
    const productsWithDuplicateSku = productResults.filter((product) => {
      // On an edit page for an existing product, if the current product sku is the same as
      // what is in the SKU input. In that case, return false
      if (product.sku! === this.product.sku && !!this.product.id) {
        return false
      }

      return product.sku! === inputSku
    })

    this.setState({ productsWithDuplicateSku })
  }

  loadProduct = async (productId: string) => {
    if (productId) {
      try {
        const product = await API.getProductById(this.company.id, productId, true)
        if ('locationOverrides' in product) {
          delete product.locationOverrides
        }
        if (isSystemProduct(product)) {
          browserHistory.push(Routes.DASHBOARD_PRODUCTS(this.company.id))
          return
        }
        if (!product.isDeleted && product.parentProductId) {
          browserHistory.push(Routes.EDIT_PRODUCT(this.company.id, product.parentProductId))
          return
        }

        product.childProducts = product.isDeleted
          ? product.childProducts
          : product.childProducts.filter((child: any) => !child.isDeleted)

        for (const child of product.childProducts) {
          if ('locationOverrides' in child) {
            delete child.locationOverrides
          }
        }

        this.product.populateFields(product)

        this.loadParentProduct(product)

        this.product.unsetChanged()
      } catch (error) {
        message.error(parseErrorMsg(error) || 'There was an error finding your product')
      }
    }
  }

  loadParentProduct = async (product: INestedProduct) => {
    if (product.isDeleted && !product.isMasterProduct) {
      const parentProduct = await API.getProductById(this.company.id, product.parentProductId!)
      this.setState({ parentProduct })
    }
  }

  addProductDetailFromCatalog = (supplierId?: number): void => {
    // Populate product details from AGLC or OCS
    this.product.sku = this.product.sku || null
    this.product.isMasterProduct = true
    this.product.supplierId = supplierId || null
    this.product.setInventoryMode('prepack')
    this.product.imageUrl = this.product.imageUrl || null
  }

  createNewChildProduct = (
    products: ProvinceProductModels.IProvinceProductContract[],
  ): CreateChildProduct[] => {
    return products.map((childProduct) => {
      const newChildProduct = new CreateChildProduct(this.product)
      newChildProduct.setName(GrRegex.replaceLatin1Space(childProduct.name))
      newChildProduct.setBarcode(childProduct.barcode || null)
      newChildProduct.setSKU(childProduct.sku)
      newChildProduct.setCannabisWeight(
        childProduct.cannabisWeight !== null ? childProduct.cannabisWeight : null,
      )
      newChildProduct.setWeight(childProduct.weight !== null ? childProduct.weight : null)
      newChildProduct.setCannabisVolume(childProduct.volume !== null ? childProduct.volume : null)
      newChildProduct.setDescription(childProduct.description)
      newChildProduct.metaData.setValue(MetaDataReservedKeys.MAX_THC, childProduct.maxThc)
      newChildProduct.metaData.setValue(MetaDataReservedKeys.MIN_THC, childProduct.minThc)
      newChildProduct.metaData.setValue(MetaDataReservedKeys.MAX_CBD, childProduct.maxCbd)
      newChildProduct.metaData.setValue(MetaDataReservedKeys.MIN_CBD, childProduct.minCbd)
      newChildProduct.metaData.setValue(
        MetaDataReservedKeys.UNIT,
        childProduct.thcCbdUnit || UnitModels.ThcCbdUnit.PERCENT,
      )
      newChildProduct.metaData.setValue(MetaDataReservedKeys.SHOW_MAX_MIN, true)
      newChildProduct.setImageUrl(childProduct.imageUrl || null)

      return newChildProduct
    })
  }

  createNewChildProductV2 = (products: IGlobalProduct[]): CreateChildProduct[] => {
    return products.map((childProduct) => {
      const quantity = childProduct.itemsPerPack || 1
      const netWeight = childProduct.netWeight || 0
      const netContent = netWeight / quantity
      const newChildProduct = new CreateChildProduct(this.product)
      newChildProduct.setName(GrRegex.replaceLatin1Space(childProduct.name))
      newChildProduct.setBarcode(childProduct.barcode || null)
      newChildProduct.setGlobalProductId(childProduct.barcode || null)
      newChildProduct.setSKU(getSkuFromGlobalProductV2(childProduct))
      newChildProduct.setCannabisWeight(childProduct.cannabisWeight)
      newChildProduct.setWeight(netWeight)
      newChildProduct.setDescription(childProduct.description || '')
      newChildProduct.metaData.setValue(MetaDataReservedKeys.QUANTITY, quantity)
      newChildProduct.metaData.setValue(MetaDataReservedKeys.NET_CONTENT, netContent)
      newChildProduct.metaData.setValue(MetaDataReservedKeys.MAX_THC, childProduct.potency.maxThc)
      newChildProduct.metaData.setValue(MetaDataReservedKeys.MIN_THC, childProduct.potency.minThc)
      newChildProduct.metaData.setValue(MetaDataReservedKeys.MAX_CBD, childProduct.potency.maxCbd)
      newChildProduct.metaData.setValue(MetaDataReservedKeys.MIN_CBD, childProduct.potency.minCbd)
      newChildProduct.metaData.setValue(
        MetaDataReservedKeys.NET_CONTENT_UNIT,
        childProduct.potency.unit || UnitModels.ThcCbdUnit.PERCENT,
      )
      newChildProduct.metaData.setValue(MetaDataReservedKeys.SHOW_MAX_MIN, true)
      if (
        newChildProduct.metaData.minCBD &&
        !newChildProduct.metaData.maxCBD &&
        newChildProduct.metaData.showMaxMin
      ) {
        newChildProduct.metaData.setValue(MetaDataReservedKeys.MAX_CBD, childProduct.potency.minCbd)
      }
      newChildProduct.setImageUrl(childProduct.images.length > 0 ? childProduct.images[0] : null)
      return newChildProduct
    })
  }

  populateProduct = (
    products: ProvinceProductModels.IProvinceProductContract[],
    supplierId?: number,
  ) => {
    this.addProductDetailFromCatalog(supplierId)
    const firstChildProduct = this.product.childProducts[0]
    if (
      this.product.childProducts.length === 1 &&
      firstChildProduct.id === null &&
      firstChildProduct.name === '' &&
      firstChildProduct.price === 0 &&
      firstChildProduct.sku === null
    ) {
      this.product.clearAllChildProducts()
    }

    if (!this.product.imageUrl) {
      const imageUrl = this.getFirstAvailableProductImage(products)
      this.product.setImageUrl(imageUrl)
    }

    const newChildProducts = this.createNewChildProduct(products)

    this.product.childProducts = [...this.product.childProducts, ...newChildProducts]

    this.product.updateActiveStatus()
  }

  populateStandaloneProductFromGlobalProduct = (
    products: IGlobalProduct[],
    supplierId?: number,
  ) => {
    this.addProductDetailFromCatalog(supplierId)
    const firstChildProduct = this.product.childProducts[0]
    if (
      this.product.childProducts.length === 1 &&
      firstChildProduct.id === null &&
      firstChildProduct.name === '' &&
      firstChildProduct.price === 0 &&
      firstChildProduct.sku === null
    ) {
      this.product.clearAllChildProducts()
    }

    if (!this.product.imageUrl) {
      const imageUrl = this.getFirstAvailableGlobalProductImage(products)
      this.product.setImageUrl(imageUrl)
    }

    const newChildProducts = this.createNewChildProductV2(products)
    this.product.childProducts = [...this.product.childProducts, ...newChildProducts]

    this.product.updateActiveStatus()
  }

  populateStandaloneProduct = (
    products: ProvinceProductModels.IProvinceProductContract[],
    supplierId?: number,
  ) => {
    const standaloneProduct = products[0]
    this.product.setSKU(standaloneProduct.sku)
    this.product.setName(GrRegex.replaceLatin1Space(standaloneProduct.name))
    this.product.setInventoryMode(true)
    this.product.setSupplierId(supplierId || null)
    this.product.setImageUrl(standaloneProduct.imageUrl)
    this.product.setWeight(standaloneProduct.weight)
    this.product.setCannabisVolume(standaloneProduct.volume)
    this.product.setCannabisWeight(standaloneProduct.cannabisWeight)
    this.product.setBarcode(standaloneProduct.barcode)
    this.product.setDescription(standaloneProduct.description)
    this.product.metaData.setValue(MetaDataReservedKeys.MAX_THC, standaloneProduct.maxThc)
    this.product.metaData.setValue(MetaDataReservedKeys.MIN_THC, standaloneProduct.minThc)
    this.product.metaData.setValue(MetaDataReservedKeys.MAX_CBD, standaloneProduct.maxCbd)
    this.product.metaData.setValue(MetaDataReservedKeys.MIN_CBD, standaloneProduct.minCbd)
    this.product.metaData.setValue(
      MetaDataReservedKeys.UNIT,
      standaloneProduct.thcCbdUnit || UnitModels.ThcCbdUnit.PERCENT,
    )
    this.product.metaData.setValue(MetaDataReservedKeys.SHOW_MAX_MIN, true)
    if (
      this.product.metaData.minCBD &&
      !this.product.metaData.maxCBD &&
      this.product.metaData.showMaxMin
    ) {
      this.product.metaData.setValue(MetaDataReservedKeys.MAX_CBD, standaloneProduct.minCbd)
    }
  }

  populateVariantProductFromGlobalProduct = (products: IGlobalProduct[], supplierId?: number) => {
    const product = products[0]
    const quantity = product.itemsPerPack || 1
    const netWeight = product.netWeight || 0
    const netContent = netWeight / quantity
    const sku = getSkuFromGlobalProduct(product, this.company)
    this.product.setSKU(sku ?? '')
    this.product.setName(GrRegex.replaceLatin1Space(product.name))
    this.product.setInventoryMode(true)
    this.product.setSupplierId(supplierId ?? null)
    this.product.setGlobalProductId(product.barcode)
    this.product.setImageUrl(product.images[0] ?? null)
    this.product.setWeight(product.netWeight)
    this.product.setCannabisWeight(product.cannabisWeight)
    this.product.setBarcode(product.barcode)
    this.product.setDescription(product.description ?? '')
    this.product.metaData.setValue(MetaDataReservedKeys.QUANTITY, quantity)
    this.product.metaData.setValue(MetaDataReservedKeys.NET_CONTENT, netContent)
    this.product.metaData.setValue(MetaDataReservedKeys.MAX_THC, product.potency.maxThc)
    this.product.metaData.setValue(MetaDataReservedKeys.MIN_THC, product.potency.minThc)
    this.product.metaData.setValue(MetaDataReservedKeys.MAX_CBD, product.potency.maxCbd)
    this.product.metaData.setValue(MetaDataReservedKeys.MIN_CBD, product.potency.minCbd)
    this.product.metaData.setValue(
      MetaDataReservedKeys.NET_CONTENT_UNIT,
      product.potency.unit || UnitModels.ThcCbdUnit.PERCENT,
    )
    this.product.metaData.setValue(MetaDataReservedKeys.SHOW_MAX_MIN, true)
    if (
      this.product.metaData.minCBD &&
      !this.product.metaData.maxCBD &&
      this.product.metaData.showMaxMin
    ) {
      this.product.metaData.setValue(MetaDataReservedKeys.MAX_CBD, product.potency.minCbd)
    }
  }

  getFirstAvailableProductImage = (products: ProvinceProductModels.IProvinceProductContract[]) => {
    const productWithImageUrl = products.find((product) => product.imageUrl)
    return productWithImageUrl ? productWithImageUrl.imageUrl : null
  }

  getFirstAvailableGlobalProductImage = (products: IGlobalProduct[]) => {
    const productWithImageUrl = products.find((product) => product.images.length > 0)
    return productWithImageUrl ? productWithImageUrl.images[0] : null
  }

  updateProduct = async (companyId: number, productId: string) => {
    const updateObject = this.product.networkObject as any
    updateObject.id = this.product.id

    if ('locationOverrides' in updateObject) {
      delete updateObject.locationOverrides
    }

    for (const child of updateObject.childProducts) {
      if ('locationOverrides' in child) {
        delete child.locationOverrides
      }
    }

    const canUseCannabinoidPerLot = this.props.currentCompanyStore?.canUseCannabinoidPerBatch
    try {
      const product = await this.props.currentCompanyStore!.productStore!.editProduct(
        companyId,
        productId,
        updateObject,
        canUseCannabinoidPerLot,
      )
      Track.track('dashboard:products_updated', product)
      message.success(`Successfully updated`)
      this.setState({ isSaving: false })
      this.product.populateFields(product)
      this.product.unsetChanged()
      this.product.needsPricingGroupRefresh = false
      this.props.currentCompanyStore?.getTags()
    } catch (error: any) {
      const { message } = error.response.data
      const errorMessage = message.includes('already exists') ? message : `Variant ${message}`
      this.setState({
        isSaving: false,
        errorMessage: errorMessage || 'There was an error updating this product',
      })
    }
  }

  createNewProduct = async (companyId: number) => {
    const createObject = this.product.networkObject
    try {
      const product = await this.props.currentCompanyStore!.productStore!.addProduct(
        companyId,
        createObject,
      )
      Track.track('dashboard:products_created', product)
      message.success(`Successfully added`)
      this.setState({ isSaving: false })
      this.product.unsetChanged()
      this.product.needsPricingGroupRefresh = false
      this.props.currentCompanyStore?.getTags()
      browserHistory.replace(Routes.EDIT_PRODUCT(companyId, product.id))
    } catch (error: any) {
      const { message } = error.response.data
      const errorMessage = message.includes('already exists') ? message : `Variant ${message}`
      this.setState({
        isSaving: false,
        errorMessage: errorMessage || 'There was an error creating this product',
      })
    }
  }

  onSave = async () => {
    this.setState({ isImageUploadingToCloudinary: true })
    if (this.product.imageUrl && !this.product.imageUrl.includes(CLOUDINARY)) {
      try {
        const cloudinaryUrl = await uploadImageAndGetCloudinaryUrl(
          this.product.imageUrl,
          this.props.currentCompanyStore!,
        )
        this.product.setImageUrl(cloudinaryUrl)
      } catch (error) {
        this.product.setImageUrl(null)
        console.log(error)
      }
    }
    await this.product.setInheritedProperties(this.props.currentCompanyStore)
    this.setState({ isImageUploadingToCloudinary: false })
    if (!this.product.categoryId) {
      if (!window.confirm('No category selected. Are you sure?')) {
        return
      }
    }

    this.setState({ isSaving: true, errorMessage: undefined })

    const metaDataReservedKeysMap = new Map<string, string>()
    Object.values<string>(MetaDataModels.MetaDataReservedKeys).forEach((value) =>
      metaDataReservedKeysMap.set(value, value),
    )

    if (this.product.isMasterProduct) {
      const { childProducts } = this.product
      childProducts.forEach((child) => {
        const { metaData } = child
        child.metaData = this.shouldAddUnitsMetaData(metaData, metaDataReservedKeysMap)
          ? this.addDefaultUnitToMetaData(metaData)
          : child.metaData
      })
    } else {
      const { metaData } = this.product
      this.product.metaData = this.shouldAddUnitsMetaData(metaData, metaDataReservedKeysMap)
        ? this.addDefaultUnitToMetaData(metaData)
        : this.product.metaData
    }

    if (this.product.id) {
      await this.updateProduct(this.company.id, this.product.id)
    } else {
      await this.createNewProduct(this.company.id)
    }

    this.product.unsetChanged()
    window.onbeforeunload = null
  }

  addDefaultUnitToMetaData = (metaData: ProductMetaData) => {
    metaData.metaData.push(new ProductMetaDataItem(MetaDataReservedKeys.UNIT, '%'))

    return metaData
  }

  shouldAddUnitsMetaData = (metaData: ProductMetaData, reservedKeysMap: Map<string, string>) => {
    return (
      metaData.metaData &&
      metaData.metaData.some((meta) => reservedKeysMap.get(meta.metaKey)) &&
      !metaData.metaData.find((meta) => meta.metaKey === MetaDataReservedKeys.UNIT)
    )
  }

  blockSaving = (blockSaving: boolean, errorMessage?: string) => {
    if (blockSaving) {
      this.setState({ savingBlocked: true, errorMessage })
    } else {
      this.setState({ savingBlocked: false, errorMessage: undefined })
    }
  }

  setVariantsSectionRef = (el: HTMLDivElement) => {
    this.setState({ variantsSectionRef: el })
  }

  autoScrollToVariants = () => {
    this.setState({}, () => {
      this.state.variantsSectionRef?.scrollIntoView({ behavior: 'smooth', block: 'end' })
    })
  }

  get allActiveKeys() {
    return Array.from(
      {
        length: this.product.childProducts.length,
      },
      (_, index) => index.toString(),
    )
  }

  render() {
    const categories = this.props.currentCompanyStore!.productStore!.flattenedCategories || []

    const helmet = (
      <Helmet>
        <title>Products | Edit Product</title>
        <meta name='page-loaded' content='true' />
      </Helmet>
    )

    const saveProductFirstWarning: JSX.Element = (
      <div className='mb-2 text-muted'>Please save your product first to edit this section</div>
    )
    if (this.props.params.productId && !this.product.id) {
      return (
        <div>
          {helmet}
          <LoadingAnimation />
        </div>
      )
    }
    return (
      <AppView column={true} gray={false}>
        {helmet}
        <AppHeader
          title={this.product.id ? `Edit ${this.product.name}` : 'Add a product'}
          backVisible={true}
          onBackClick={() =>
            browserHistory.push(
              Routes.DASHBOARD_PRODUCTS_LISTING(this.company.id, window.location.search),
            )
          }
          noMenuButton={true}
        >
          <span className='spaced-button-wrapper'>
            <HelpLinkMenu
              helpLinkMenus={[
                {
                  href: HelpLinks.PRODUCTS_ADD_EDIT,
                  title: 'Product management',
                },
                {
                  href: HelpLinks.PRODUCTS_ADD_FROM_DB,
                  title: 'Provincial product database',
                },
              ]}
            />

            <BlazeButton
              buttonProps={{
                style: { width: '200px' },
                type: 'primary',
                icon: undefined,
                onClick: this.onSave,
                loading: this.state.isSaving || this.state.isImageUploadingToCloudinary,
                disabled:
                  this.state.isSaving ||
                  this.state.savingBlocked ||
                  this.product.isDeleted ||
                  this.state.isImageUploadingToCloudinary,
              }}
              titleProps={{
                title: this.state.isImageUploadingToCloudinary
                  ? 'Uploading images'
                  : this.product.changed
                  ? 'Save (unsaved changes)'
                  : 'Save',
              }}
            />
          </span>
        </AppHeader>

        <AppView column={true} gray={false}>
          <Row className='d-flex justify-content-center' style={{ paddingBottom: 450 }}>
            <Col xxl={20} span={22}>
              <Row>
                {this.state.parentProduct && !this.state.parentProduct.isDeleted && (
                  <div className='mt-4'>
                    <ParentWarning parentProduct={this.state.parentProduct} />
                  </div>
                )}

                {this.product.id && this.product.isDeleted && (
                  <div className='mt-4'>
                    <BlazeAlert type='warning' showIcon>
                      This product is deleted and cannot be edited
                    </BlazeAlert>
                  </div>
                )}

                {!this.product.isDeleted && !this.product.isGiftCard && (
                  <div className='mt-4'>
                    <TemplateSection
                      populateProduct={
                        this.product.isMasterProduct
                          ? this.populateProduct
                          : this.populateStandaloneProduct
                      }
                      populateGlobalProduct={
                        this.product.isMasterProduct
                          ? this.populateStandaloneProductFromGlobalProduct
                          : this.populateVariantProductFromGlobalProduct
                      }
                      product={this.product}
                      currentCompanyStore={this.props.currentCompanyStore}
                      activeKeys={this.state.activeKeys}
                      setActiveKeys={this.setActiveKeys}
                      autoScrollToVariants={this.autoScrollToVariants}
                    />
                  </div>
                )}
              </Row>

              <Row gutter={26} className='mt-4'>
                <Col xxl={18} xl={18}>
                  <InventorySection
                    product={this.product}
                    isEditMode={!!this.product.id}
                    currentCompanyStore={this.props.currentCompanyStore}
                  />

                  <BasicInformationSection
                    product={this.product}
                    pricingGroups={this.state.pricingGroups}
                    searchDuplicateSkus={this.searchDuplicateSkus}
                    currentCompanyStore={this.props.currentCompanyStore}
                    saveProductFirstWarning={saveProductFirstWarning}
                    isEditMode={!!this.product.id}
                    blockSaving={this.blockSaving}
                  />

                  <DetailsSection
                    product={this.product}
                    saveProductFirstWarning={saveProductFirstWarning}
                    blockSaving={this.blockSaving}
                  />

                  {this.product.isMasterProduct && (
                    <VariantsSection
                      product={this.product}
                      activeKeys={this.state.activeKeys}
                      pricingGroups={this.state.pricingGroups}
                      currentCompanyStore={this.props.currentCompanyStore}
                      companyId={this.company.id}
                      searchDuplicateSkus={this.searchDuplicateSkus}
                      populateProduct={this.populateProduct}
                      populateGlobalProduct={this.populateVariantProductFromGlobalProduct}
                      blockSaving={this.blockSaving}
                      loadProduct={this.loadProduct}
                      setActiveKeys={this.setActiveKeys}
                      allActiveKeys={this.allActiveKeys}
                      setVariantsSectionRef={this.setVariantsSectionRef}
                      autoScrollToVariants={this.autoScrollToVariants}
                    />
                  )}
                </Col>

                <Col xxl={6} xl={6}>
                  <UploadImageSection
                    product={this.product}
                    className='card mb-4 text-center'
                    imageStyle={{ width: '100%' }}
                    iconWrapperStyle={{ height: '200px' }}
                    iconStyle={{ lineHeight: '200px' }}
                  />

                  <DashboardImageCard
                    title='Sample Direct'
                    imageUrl={SampleDirectLogo}
                    items={['Order samples', 'Check upcoming drops', "See what's trending"]}
                    buttonText='Check out'
                    buttonUrl={Routes.DASHBOARD_SAMPLE_DIRECT(
                      this.props.currentCompanyStore?.company?.id || 0,
                    )}
                  />

                  <div style={{ position: 'sticky', top: '1rem' }}>
                    <WarningSection
                      company={this.company}
                      product={this.product}
                      categories={categories}
                      productsWithDuplicateSku={this.state.productsWithDuplicateSku}
                      errorMessage={this.state.errorMessage}
                    />

                    {this.product.isMasterProduct && (
                      <VariantStatusSection product={this.product} />
                    )}
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </AppView>
      </AppView>
    )
  }
}
